import type { ApiMenuItem } from "@/features/theme/types/brandTheme.type";

type Candidate = {
  key: string
  basePath: string
  params: [string, string][]
};

const normalizePath = (path: string) => (path.length > 1 ? path.replace(/\/+$/, "") : path);

// Pages living under another entry's route: deal orders are listed in « Mes commandes ».
const PATH_ALIASES: [from: string, to: string][] = [["/bon-plans/commandes", "/boutique/commandes"]];

const resolveAlias = (pathname: string) => {
  const alias = PATH_ALIASES.find(([from]) => pathname === from || pathname.startsWith(`${from}/`));
  return alias ? `${alias[1]}${pathname.slice(alias[0].length)}` : pathname;
};

const collectCandidates = (items: ApiMenuItem[]): Candidate[] => items.flatMap((item): Candidate[] => {
  const own: Candidate[] = [];
  // A group with its own route is rendered through its children (collapsible or icon mode).
  if (item.route && !item.hidden && !item.children?.length) {
    const [path, query = ""] = item.route.split("?");
    own.push({ key: item.key, basePath: normalizePath(path), params: [...new URLSearchParams(query).entries()] });
  }
  return [...own, ...collectCandidates(item.children ?? [])];
});

const coversPath = (basePath: string, pathname: string) => {
  if (basePath === "/") return pathname === "/";
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
};

const queryMatches = (params: [string, string][], search: URLSearchParams) =>
  params.every(([name, value]) => search.get(name) === value);

/**
 * Key of the single menu entry to highlight for the current location.
 *
 * - an entry covers its route and every page below it (`/fichiers` covers
 *   `/fichiers/NODE_…`); the dashboard `/` only covers itself;
 * - the longest route wins: `/boutique/commandes/7` highlights « Mes commandes »,
 *   not « Boutique »;
 * - a query string only counts when it tells apart entries sharing a path
 *   (catalogue collections `/catalogue?collection=…`); otherwise « Mon profil »
 *   (`/mon-profil?tab=autorisations`) would only light up on one tab.
 */
export const getActiveMenuKey = (items: ApiMenuItem[], pathname: string, search: string): string | null => {
  const currentPath = resolveAlias(normalizePath(pathname));
  const searchParams = new URLSearchParams(search);
  const candidates = collectCandidates(items);

  const covering = candidates.filter((candidate) => coversPath(candidate.basePath, currentPath));
  if (covering.length === 0) return null;

  const longest = Math.max(...covering.map((candidate) => candidate.basePath.length));
  const contenders = covering.filter((candidate) => candidate.basePath.length === longest);
  if (contenders.length === 1) return contenders[0].key;

  const winner = contenders.find((candidate) => candidate.params.length > 0 && queryMatches(candidate.params, searchParams))
    ?? contenders.find((candidate) => candidate.params.length === 0);
  return winner?.key ?? null;
};
